import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useMyServices } from "@/lib/services";
import { RENEW_MESSAGE, useMySubscriptions } from "@/lib/subscriptions";
import {
  PARKING_TYPES,
  VEHICLE_TYPES,
  type ParkingType,
  type VehicleType,
} from "@/lib/parking-mock-data";

export const Route = createFileRoute("/_authenticated/partner/parking/new")({
  head: () => ({
    meta: [
      { title: "Add parking place · Landoo" },
      { name: "description", content: "List a parking place with slots, pricing and facilities." },
    ],
  }),
  component: NewParkingPage,
});

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";

function NewParkingPage() {
  const { user } = useAuth();
  const { canList, isLoading: svcLoading } = useMyServices();
  const { isExpired } = useMySubscriptions();
  const parkingExpired = canList("parking") && isExpired("parking");
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState(false);
  const [vehicles, setVehicles] = useState<VehicleType[]>(["car"]);
  const [f, setF] = useState({
    title: "",
    provider_name: "",
    description: "",
    address: "",
    city: "",
    latitude: "",
    longitude: "",
    location_precision: "approximate" as "exact" | "approximate",
    parking_type: "open" as ParkingType,
    total_slots: "",
    available_slots: "",
    price_hourly: "",
    price_daily: "",
    price_monthly: "",
    opening_time: "",
    closing_time: "",
    open_24h: false,
    cctv: false,
    security: false,
    lighting: false,
    ev_charging: false,
    entry_instructions: "",
    exit_instructions: "",
    image_url: "",
    contact_visible: false,
    enquiry_enabled: true,
    booking_enabled: false,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));

  const toggleVehicle = (v: VehicleType) =>
    setVehicles((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const submit = async (status: "draft" | "pending") => {
    if (!user) return;
    if (!f.title) return toast.error("Parking title is required");
    if (!terms) return toast.error("Please accept the parking listing terms");
    setSubmitting(true);
    const { error } = await supabase.from("parking_listings").insert({
      owner_id: user.id,
      title: f.title,
      provider_name: f.provider_name || null,
      description: f.description || null,
      address: f.address || null,
      city: f.city || null,
      latitude: f.latitude ? Number(f.latitude) : null,
      longitude: f.longitude ? Number(f.longitude) : null,
      location_precision: f.location_precision,
      parking_type: f.parking_type,
      vehicle_types: vehicles,
      total_slots: f.total_slots ? Number(f.total_slots) : 0,
      available_slots: f.available_slots ? Number(f.available_slots) : 0,
      price_hourly: f.price_hourly ? Number(f.price_hourly) : null,
      price_daily: f.price_daily ? Number(f.price_daily) : null,
      price_monthly: f.price_monthly ? Number(f.price_monthly) : null,
      opening_time: f.opening_time || null,
      closing_time: f.closing_time || null,
      open_24h: f.open_24h,
      cctv: f.cctv,
      security: f.security,
      lighting: f.lighting,
      ev_charging: f.ev_charging,
      entry_instructions: f.entry_instructions || null,
      exit_instructions: f.exit_instructions || null,
      image_url: f.image_url || null,
      contact_visible: f.contact_visible,
      enquiry_enabled: f.enquiry_enabled,
      booking_enabled: f.booking_enabled,
      status,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(status === "pending" ? "Submitted for review" : "Draft saved");
    nav({ to: "/partner/parking" });
  };

  if (!svcLoading && parkingExpired) {
    return (
      <DashboardShell title="Add parking place" role="Partner" items={PARTNER_NAV}>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900">
          <span className="font-semibold">Renew Subscription</span>
          <p className="mt-1 text-xs">{RENEW_MESSAGE}</p>
          <Link
            to="/partner/subscription"
            className="mt-3 inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
          >
            Renew Subscription
          </Link>
        </div>
      </DashboardShell>
    );
  }

  if (!svcLoading && !canList("parking")) {
    return (
      <DashboardShell title="Add parking place" role="Partner" items={PARTNER_NAV}>
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          The Parking service is not active on your account. Parking has its own registration,
          verification, agreement, subscription and admin approval — activate it from My services
          before listing a parking place.
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Add parking place" role="Partner" items={PARTNER_NAV}>
      <form
        className="mx-auto max-w-2xl space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft"
        onSubmit={(e) => {
          e.preventDefault();
          submit("pending");
        }}
      >
        <div className="rounded-xl bg-parking/10 px-3 py-2 text-[11px] font-semibold text-parking">
          Parking listing — bedrooms, bathrooms, furnishing and property area do not apply.
        </div>

        <Field label="Parking title">
          <input
            required
            className={inp}
            value={f.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>
        <Field label="Parking provider name">
          <input
            className={inp}
            value={f.provider_name}
            onChange={(e) => set("provider_name", e.target.value)}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Parking location / address">
            <input
              className={inp}
              value={f.address}
              onChange={(e) => set("address", e.target.value)}
            />
          </Field>
          <Field label="City">
            <input className={inp} value={f.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Map pin — latitude">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.latitude}
              onChange={(e) => set("latitude", e.target.value)}
            />
          </Field>
          <Field label="Map pin — longitude">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.longitude}
              onChange={(e) => set("longitude", e.target.value)}
            />
          </Field>
          <Field label="Address display">
            <select
              className={inp}
              value={f.location_precision}
              onChange={(e) => set("location_precision", e.target.value as "exact" | "approximate")}
            >
              <option value="approximate">Approximate</option>
              <option value="exact">Exact</option>
            </select>
          </Field>
        </div>

        <Field label="Parking type">
          <select
            className={inp}
            value={f.parking_type}
            onChange={(e) => set("parking_type", e.target.value as ParkingType)}
          >
            {PARKING_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Vehicle types accepted">
          <div className="flex flex-wrap gap-1.5">
            {VEHICLE_TYPES.map((v) => (
              <button
                key={v.value}
                type="button"
                onClick={() => toggleVehicle(v.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  vehicles.includes(v.value)
                    ? "bg-parking text-white shadow-soft"
                    : "bg-secondary/70 text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Total slots">
            <input
              type="number"
              className={inp}
              value={f.total_slots}
              onChange={(e) => set("total_slots", e.target.value)}
            />
          </Field>
          <Field label="Available slots">
            <input
              type="number"
              className={inp}
              value={f.available_slots}
              onChange={(e) => set("available_slots", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Hourly price (₹)">
            <input
              type="number"
              className={inp}
              value={f.price_hourly}
              onChange={(e) => set("price_hourly", e.target.value)}
            />
          </Field>
          <Field label="Daily price (₹)">
            <input
              type="number"
              className={inp}
              value={f.price_daily}
              onChange={(e) => set("price_daily", e.target.value)}
            />
          </Field>
          <Field label="Monthly price (₹)">
            <input
              type="number"
              className={inp}
              value={f.price_monthly}
              onChange={(e) => set("price_monthly", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Opening time">
            <input
              type="time"
              className={inp}
              value={f.opening_time}
              onChange={(e) => set("opening_time", e.target.value)}
              disabled={f.open_24h}
            />
          </Field>
          <Field label="Closing time">
            <input
              type="time"
              className={inp}
              value={f.closing_time}
              onChange={(e) => set("closing_time", e.target.value)}
              disabled={f.open_24h}
            />
          </Field>
          <Toggle
            label="24-hour access"
            checked={f.open_24h}
            onChange={(v) => set("open_24h", v)}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <Toggle label="CCTV" checked={f.cctv} onChange={(v) => set("cctv", v)} />
          <Toggle label="Security" checked={f.security} onChange={(v) => set("security", v)} />
          <Toggle label="Lighting" checked={f.lighting} onChange={(v) => set("lighting", v)} />
          <Toggle
            label="EV charging"
            checked={f.ev_charging}
            onChange={(v) => set("ev_charging", v)}
          />
        </div>

        <Field label="Entry instructions">
          <textarea
            rows={2}
            className={inp}
            value={f.entry_instructions}
            onChange={(e) => set("entry_instructions", e.target.value)}
          />
        </Field>
        <Field label="Exit instructions">
          <textarea
            rows={2}
            className={inp}
            value={f.exit_instructions}
            onChange={(e) => set("exit_instructions", e.target.value)}
          />
        </Field>
        <Field label="Parking photo URL">
          <input
            className={inp}
            value={f.image_url}
            onChange={(e) => set("image_url", e.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Description">
          <textarea
            rows={3}
            className={inp}
            value={f.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <div className="grid gap-2 sm:grid-cols-3">
          <Toggle
            label="Show contact"
            checked={f.contact_visible}
            onChange={(v) => set("contact_visible", v)}
          />
          <Toggle
            label="Enquiry enabled"
            checked={f.enquiry_enabled}
            onChange={(v) => set("enquiry_enabled", v)}
          />
          <Toggle
            label="Booking (coming soon)"
            checked={f.booking_enabled}
            onChange={(v) => set("booking_enabled", v)}
          />
        </div>

        <label className="flex items-start gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5"
          />
          I confirm I am authorised to list this parking space and accept the parking listing terms.
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => submit("draft")}
            disabled={submitting}
            className="flex-1 rounded-xl border border-border bg-background py-2.5 text-sm font-semibold text-brown-dark disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-xl bg-parking py-2.5 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </form>
    </DashboardShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium text-brown-dark">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
